import React, {FC, useContext} from "react"
import {Col, Row} from "@irati/ui/src/Layout"
import {WellnessMedia, WellnessMediaType, MarketingProfile, UpdateMarketingProfileInput} from "@irati/types"
import {InjectIntl, injectIntl, useIntl} from "@irati/utils/lang"
import DrawerContext from "@irati/ui/src/AppContainer/Context/DrawerContext"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import {getDomain} from "@irati/utils/links"
import authentication from "@irati/auth"
import tracking from "@irati-tracking/platform/platform"
import {UTMMedium, UTMSource, addUTMToLink} from "@irati-web/platform/src/utils/shared"

import LiveVideoPlayer from "./LiveVideoPlayer"
import MediaDetails from "./MediaDetails"
import Reminder from "./LiveVideoReminder"
import RelatedMediaList from "./RelatedMediaList"
import PremiumMediaVideoSection from "./PremiumMedia/PremiumMediaVideoSection"
import PremiumMediaAudioSection from "./PremiumMedia/PremiumMediaAudioSection"
import ExplorePremiumContent from "./ExplorePremiumContent"
import RentStaticMedia from "./RentStaticMedia"
import {ArticleWrapper} from "./PremiumMedia/styled"

interface Props extends InjectIntl {
  media: WellnessMedia
  marketingProfile: MarketingProfile
  handleClick: (marketingProfileInput: UpdateMarketingProfileInput) => void
  liveVideoInProgress: boolean
}

const MediaContent: FC<Props> = ({media, marketingProfile, liveVideoInProgress, handleClick}) => {
  const {setDrawerOpen} = useContext(DrawerContext)
  const {formatMessage} = useIntl()

  const isPremiumContent = !!media.price
  const isMediaRented = !!media?.src?.length

  const handleCopyLink = () => {
    const drawerData = {
      url: addUTMToLink(
        `${getDomain(authentication.business().subdomain)}/wellness/details/${media.uuid}`,
        UTMSource.WELLNESS_DETAILS,
        UTMMedium.SHARE_LINK
      ),
      title: media.title,
      alertText: "platform.share.alertText",
      source: "wellness",
      type: "details",
      entity_id: media.uuid
    }
    if (navigator?.canShare?.(drawerData)) {
      setDrawerOpen(drawerData)
    } else {
      navigator.clipboard.writeText(drawerData.url).then(() => {
        tracking.share({source: "wellness", type: "details", entity_id: media.uuid})
        triggerSuccessToast(formatMessage({id: "wellness.copyVideoLink.title"}))
      })
    }
  }
  return (
    <Row>
      <Col md={8} sm={12}>
        {media.type === WellnessMediaType.Article && <ArticleWrapper thumbnail={media.thumbnail} marginTop={5} />}
        {media.type === WellnessMediaType.SingleVideo && (
          <PremiumMediaVideoSection media={media} isPremiumContent={isPremiumContent} isMediaRented={isMediaRented} />
        )}
        {media.type === WellnessMediaType.SingleAudio && (
          <PremiumMediaAudioSection media={media} isPremiumContent={isPremiumContent} isMediaRented={isMediaRented} />
        )}
        {media.type === WellnessMediaType.LiveVideo && <LiveVideoPlayer media={media} liveVideoInProgress={liveVideoInProgress} />}
        <MediaDetails
          media={media}
          liveVideoInProgress={liveVideoInProgress}
          isPremiumContent={isPremiumContent}
          onShowAlert={handleCopyLink}
        />
      </Col>
      <Col md={4} sm={12} xs={12}>
        {media.type !== WellnessMediaType.LiveVideo && isPremiumContent && !isMediaRented && <RentStaticMedia media={media} />}
        {media.type === WellnessMediaType.LiveVideo && !liveVideoInProgress && (
          <Reminder media={media} marketingProfile={marketingProfile} handleClick={handleClick} />
        )}
        {media.type !== WellnessMediaType.LiveVideo && isPremiumContent && <ExplorePremiumContent />}
        <RelatedMediaList media={media} marginTop={5} />
      </Col>
    </Row>
  )
}

export default injectIntl(MediaContent)
